import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
// material
import { Avatar, Box, Drawer, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Logo from '../../components/Logo';
import NavSection from '../../components/NavSection';
import { StyledBadge } from '../../components/StyledBadge';
import sidebarConfig from './SidebarConfig';
import { useAuthContext } from '../../context/AuthContext'; // contexto de auth

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

const AccountStyle = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: theme.palette.grey[500_12],
}));

// ----------------------------------------------------------------------

function AppSideBar({ isOpenSidebar, onCloseSidebar, userIsOnline }) {
  const { pathname } = useLocation();
  const { cliente } = useAuthContext();
  const [status, setStatus] = useState(10);
  const [statusColor, setStatusColor] = useState('#44b700');
  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname]);

  const handleChange = (event) => {
    setStatus(event.target.value);
    if (event.target.value === 10) {
      setStatusColor('#44b700');
    }
    if (event.target.value === 20) {
      setStatusColor('#fff');
    }
  };

  // 🔧 Formatador de nome: "Primeiro Último"
  const formatarNome = () => {
    if (cliente?.nome) {
      const partes = cliente.nome.trim().split(' ');
      return partes.length > 1
        ? `${partes[0]} ${partes[partes.length - 1]}`
        : cliente.nome;
    }
    return cliente?.email || 'Usuário';
  };

  const renderContent = (
    <Box sx={{ flexGrow: '1' }}>
      {/* Header logo */}
      <Box sx={{ px: 2.5, py: 3, display: 'inline-flex', alignItems: 'center' }}>
        <Logo />
        <Stack direction="column" sx={{ ml: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{ color: 'text.primary', marginLeft: '10px' }}
          >
            TECNOZAP
          </Typography>
          <Typography
            variant="caption"
            alignSelf="flex-end"
            sx={{ color: 'GrayText' }}
          >
            {`v${process.env.REACT_APP_VERSION}`}
          </Typography>
        </Stack>
      </Box>

      {/* Avatar e nome */}
      <Box sx={{ mb: 2, mx: 2.5 }}>
        <AccountStyle>
          <StyledBadge
            statusColor={userIsOnline ? '#44b700' : '#fff'}
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
          >
            <Avatar
              src={cliente?.avatar || undefined}
              alt={cliente?.nome || 'Usuário'}
            />
          </StyledBadge>
          <Box sx={{ ml: 2 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.primary',
                maxWidth: 150,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {formatarNome()}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {userIsOnline ? 'Online' : 'Offline'}
            </Typography>
          </Box>
        </AccountStyle>
      </Box>

      {/* Navegação */}
      <NavSection navConfig={sidebarConfig} />
    </Box>
  );

  return (
    <RootStyle>
      <Drawer
        open
        variant="persistent"
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            bgcolor: 'background.default',
            borderRightStyle: 'dashed',
          },
        }}
      >
        {renderContent}
      </Drawer>
    </RootStyle>
  );
}

export default AppSideBar;
